import { EAuthorableNavTab } from "../models/view/nav-tabs";
import { ISortOptionConfig } from "../models/stores/sort-work-config";
import { SortTypeIds, type SortTypeId } from "../models/stores/ui-types";
export { SortTypeIds, SortTypeId };

export interface ISortWorkConfig {
  defaultPrimarySort?: SortTypeId;
  showContextFilter?: boolean;
  sortOptions?: ISortOptionConfig[];
}

export const aiEvaluations = ["categorize-design", "custom"] as const;
export type AIEvaluation = typeof aiEvaluations[number];

export const summarizers = ["text", "image"] as const;
export type Summarizer = typeof summarizers[number];

export const commentRoles = ["student", "teacher", "researcher"] as const;
export type CommentRole = typeof commentRoles[number];

export type IUnitFiles = Record<string, IUnitFile>;
export interface IUnitFile {
  sha: string;
  type?: string;
  title?: string;
}
export interface IUnit {
  code: string;
  abbrevTitle: string;
  title: string;
  subtitle: string;
  config: IUnitConfig;
  sections: Record<string, ISection>;
  planningDocument: IPlanningDocument;
  investigations: IInvestigation[];
}

export interface IUnitConfig {
  enableHistoryRoles: string[];
  disablePublish: boolean;
  placeholderText: string;
  toolbar: IToolbarItem[];
  navTabs: INavTabs;
  stamps: IStamp[];
  settings: ISettings;
  showCommentTag: boolean;
  commentTags: Record<string, string>;
  tagPrompt: string;
  enableCommentRoles: CommentRole[];
  aiEvaluation?: AIEvaluation;
  aiPrompt: IAiPrompt;
  authorTools?: IAuthorTool[];
  showIdeasButton?: boolean;
  hide4up?: boolean;
  sortWorkConfig?: ISortWorkConfig;
  termOverrides?: Record<string, string>;
}

export interface IAuthorTool {
  id: string;
  title: string;
  isTileTool: boolean;
  iconId?: string;
}

export interface IToolbarItem {
  id: string;
  title: string;
  iconId?: string;
  isTileTool?: boolean;
  isDefault?: boolean;
}

export interface INavTabs {
  lazyLoadTabContents: boolean;
  showNavPanel: boolean;
  tabSpecs: INavTabSpec[];
}

export type AuthorableNavTab = `${EAuthorableNavTab}`;

export interface INavTabSpec {
  tab: AuthorableNavTab;
  label: string;
  teacherOnly?: boolean;
  sections?: INavTabSection[];
  hidden?: boolean;
}

export interface INavTabSection {
  initials?: string;
  title: string;
  type: string;
  dataTestHeader?: string;
  dataTestItem?: string;
  documentTypes?: string[];
  order?: string;
  showStars?: string[];
  properties?: string[];
  addDocument?: boolean;
}

export interface IStamp {
  url: string;
  width: number;
  height: number;
}

export interface ISettings {
  table: {
    numFormat: string;
    tools: (string | [string, string])[];
  };
  graph: {
    tools: string[];
    defaultAxisLabels: Record<string, string>;
    emptyPlotIsNumeric: boolean;
    scalePlotOnValueChange: boolean;
    defaultSeriesLegend: boolean;
    connectPointsByDefault: boolean;
  };
}

export interface IAiPrompt {
  systemPrompt: string;
  mainPrompt: string;
  categorizationDescription: string;
  categories: string[];
  keyIndicatorsPrompt: string;
  discussionPrompt: string;
  summarizer?: Summarizer;
}

export interface ISection {
  initials: string;
  title: string;
  placeholder: string;
}

export interface IPlanningDocument {
  enable: string;
  default: boolean;
  sectionInfo: Record<string, ISection>;
  sections: { type: string }[];
}

export interface IInvestigation {
  description: string;
  ordinal: number;
  title: string;
  introduction?: { tiles?: ITile[] };
  problems: IProblem[];
}

export interface IProblem {
  description: string;
  ordinal: number;
  title: string;
  subtitle: string;
  sections: string[];
  config?: {
    planningTemplate: IPlanningTemplate;
  };
  disabled?: any[];
}

export interface IPlanningTemplate {
  overview?: { tiles: ITile[] };
  launch?: { tiles: ITile[] };
  explore?: { tiles: ITile[] };
  summarize?: { tiles: ITile[] };
}

export interface ITile {
  id: string;
  title: string;
  content: ITileContent;
}

export interface ITileContent {
  type: string;
  format?: string;
  text?: string[];
}
